import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, UserCircle, Edit, Key, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const UsersPage = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    phone: '',
    role: 'Admin'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.username.trim() || !formData.password.trim()) {
      toast.error('Name, username, and password are required');
      return;
    }
    const cleanPhone = (formData.phone || '').replace(/\D/g, '').slice(-10);
    if (cleanPhone && cleanPhone.length !== 10) {
      toast.error('Phone must be a 10-digit mobile number');
      return;
    }

    try {
      await api.post('/users', { ...formData, phone: cleanPhone || null });
      toast.success('User created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', username: '', password: '', phone: '', role: 'Admin' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = (userData) => {
    setEditingUser(userData);
    setFormData({
      name: userData.name,
      username: userData.username,
      password: '', // Don't pre-fill password
      phone: userData.phone || '',
      role: userData.role
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !formData.username.trim()) {
      toast.error('Name and username are required');
      return;
    }
    const cleanPhone = (formData.phone || '').replace(/\D/g, '').slice(-10);
    if (cleanPhone && cleanPhone.length !== 10) {
      toast.error('Phone must be a 10-digit mobile number');
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        username: formData.username,
        role: formData.role,
        phone: cleanPhone || null,
      };
      
      // Only include password if provided
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }
      
      // Password is optional for updates

      await api.put(`/users/${editingUser.id}`, updateData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      setFormData({ name: '', username: '', password: '', phone: '', role: 'Admin' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !currentStatus });
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-700';
      case 'FrontDeskExecutive':
        return 'bg-emerald-100 text-emerald-700';
      case 'Therapist':
        return 'bg-orange-100 text-orange-700';
      case 'Teller':
        return 'bg-green-100 text-green-700';
      case 'FeedbackReviewer':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage system users and access</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="grid gap-4">
            {users.map(usr => (
              <div
                key={usr.id}
                className="bg-white border-2 border-purple-200 rounded-xl p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-3 rounded-xl">
                      <UserCircle className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">{usr.name}</h3>
                        <Badge className={getRoleBadgeColor(usr.role)}>
                          {usr.role}
                        </Badge>
                        {!usr.is_active && (
                          <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">Username: {usr.username}</p>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleEdit(usr)}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    
                    <Button
                      onClick={() => handleToggleActive(usr.id, usr.is_active)}
                      variant="outline"
                      size="sm"
                      className={usr.is_active 
                        ? "border-red-300 text-red-600 hover:bg-red-50"
                        : "border-green-300 text-green-600 hover:bg-green-50"
                      }
                    >
                      {usr.is_active ? (
                        <>
                          <UserX className="w-4 h-4 mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Mobile Number <span className="text-red-500">*</span> <span className="text-xs text-gray-500 font-normal">(used for OTP login)</span></Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">+91</span>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit mobile"
                    className="pl-12 font-mono"
                    data-testid="user-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label>Role *</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Admin">Admin</option>
                  <option value="FrontDeskExecutive">Front Desk Executive</option>
                  <option value="Therapist">Therapist</option>
                  <option value="Teller">Teller</option>
                  <option value="FeedbackReviewer">Feedback Reviewer</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Create User
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter new password or leave blank"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Mobile Number <span className="text-xs text-gray-500 font-normal">(used for OTP login)</span></Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">+91</span>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit mobile"
                    className="pl-12 font-mono"
                    data-testid="user-edit-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label>Role *</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Admin">Admin</option>
                  <option value="FrontDeskExecutive">Front Desk Executive</option>
                  <option value="Therapist">Therapist</option>
                  <option value="Teller">Teller</option>
                  <option value="FeedbackReviewer">Feedback Reviewer</option>
                </select>
              </div>

              {formData.role === 'FrontDeskExecutive' && (
                <div>
                  <Label>PIN (leave blank to keep current)</Label>
                  <Input
                    type="password"
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                    placeholder="Enter new 3-digit PIN or leave blank"
                    maxLength={3}
                    className="mt-2"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  setFormData({ name: '', username: '', password: '', phone: '', role: 'Admin' });
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                Update User
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
